import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { CallStatus, PredefinedMilisecondsTypeEnum } from '../../lib/enums';
import lang from '../../lib/language';

import iconClose from '../../assets/img/close-line.svg';
import Button from '../Button/Button';
import ClickableImage from '../ClickableImage/ClickableImage';
import CompanyLogoCheckmark from '../CompanyLogoCheckmark/CompanyLogoCheckmark';

import styles from './FinalPopupOrderMobile.module.scss';

type Props = {
  processFailedMessage: string;
  processFailedMessageSub?: string;
  processSuccessMessageTitle: string;
  toggleModal: (arg?: any) => void;
  logo?: string;
  logoURL?: string;
  callStatus?: CallStatus;
  isCancelOrder?: boolean;
  processSuccessMessageBody?: string;
  processPendingMessage?: { title: string; body: string };
  defaultMessageTitle?: string;
  defaultMessageBody?: string;
  processPartiallyRejectedMessage?: { title: string; body: string };
};

const FinalPopupOrderMobile = ({
  callStatus,
  toggleModal,
  logo,
  logoURL,
  processFailedMessage,
  processFailedMessageSub,
  processSuccessMessageBody,
  processSuccessMessageTitle,
  isCancelOrder = false,
  processPendingMessage,
  processPartiallyRejectedMessage,
  defaultMessageTitle,
  defaultMessageBody,
}: Props): JSX.Element => {
  const [modalOpened, setModalOpened] = useState<boolean>(false);

  const errorMessageRender = () => {
    if (processPartiallyRejectedMessage) {
      return (
        <>
          <h6 className={styles.title}>{processPartiallyRejectedMessage.title}</h6>
          <p className={styles.message}>{processPartiallyRejectedMessage.body}</p>
        </>
      );
    }

    return (
      <div className={styles.titleAndDesc}>
        <h6 className={styles.errorTitle}>{processFailedMessage}</h6>
        {processFailedMessageSub && <h6 className={styles.errorTitle}>{processFailedMessageSub}</h6>}
      </div>
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setModalOpened(true);
    }, PredefinedMilisecondsTypeEnum.oneSecond);

    return () => {
      clearTimeout(timer);
    };
  }, []); // eslint-disable-line

  const getModalBody = (): JSX.Element => {
    if (callStatus === CallStatus.READY) {
      return (
        <div className={styles.messageAndLogo}>
          {(logo || logoURL) && <CompanyLogoCheckmark url={logoURL} logo={logo} />}
          <div className={styles.titleAndDesc}>
            <h6 className={styles.title}>{processSuccessMessageTitle}</h6>
            {processSuccessMessageBody && <p className={styles.message}>{processSuccessMessageBody}</p>}
          </div>
        </div>
      );
    }

    if (processPendingMessage) {
      return (
        <div className={styles.messageAndLogo}>
          {(logo || logoURL) && <CompanyLogoCheckmark url={logoURL} logo={logo} />}
          <div className={styles.titleAndDesc}>
            <h6 className={styles.title}>{processPendingMessage.title}</h6>
            <p className={styles.message}>{processPendingMessage.body}</p>
          </div>
        </div>
      );
    }
    if (defaultMessageTitle) {
      return (
        <div className={styles.messageAndLogo}>
          {(logo || logoURL) && <CompanyLogoCheckmark url={logoURL} logo={logo} />}
          <div className={styles.titleAndDesc}>
            <h6 className={styles.title}>{defaultMessageTitle}</h6>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.messageAndLogo}>
        {(logo || logoURL) && <CompanyLogoCheckmark isCancel url={logoURL} logo={logo} />}
        {errorMessageRender()}
      </div>
    );
  };

  return (
    <div className={classNames(styles.popupWrapper, { [styles.cancelOrderWrapper]: isCancelOrder })}>
      {modalOpened && (
        <div className={styles.container}>
          <div className={styles.logoAndNameWrapper}>
            <div className={styles.closeImageWrapper}>
              <div className={styles.emptyContainer} />
              <ClickableImage src={iconClose} onClick={() => toggleModal(false)} className={styles.closeImage} />
            </div>
            {getModalBody()}
          </div>
          <Button className={styles.closeBtn} fullWidth onClick={() => toggleModal(false)}>
            {lang.commonOkay()}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FinalPopupOrderMobile;
